import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const DEFAULT_VAPID_PUBLIC_KEY = 'BLye6gutl6bbUxejFb44eYVVeEmUcqTUe3m4ue0-EHQe5g4bEX6cDagqmmoK-qMBwgL3JY_94NUM3237mrl7pDo';
const DOROPARTY_ORIGIN = 'https://doroparty.com';

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function requireDoroPartyOrigin() {
  if (window.location.origin !== DOROPARTY_ORIGIN) {
    throw new Error('알림은 doroparty.com에서만 등록할 수 있습니다. 정식 주소로 다시 접속해주세요.');
  }
}

function notifyPushSubscriptionChanged() {
  window.dispatchEvent(new CustomEvent('dororaid:push-subscription-changed'));
}

function normalizeDoroPartyUrl(value = '/') {
  try {
    const url = new URL(String(value || '/'), DOROPARTY_ORIGIN);
    return url.origin === DOROPARTY_ORIGIN ? url.href : `${DOROPARTY_ORIGIN}/`;
  } catch {
    return `${DOROPARTY_ORIGIN}/`;
  }
}

function base64UrlToUint8Array(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

function normalizeUnionId(unionId) {
  const normalized = String(unionId || '').trim();
  if (!normalized) throw new Error('현재 유니온 정보를 확인할 수 없습니다. 다시 로그인해주세요.');
  return normalized;
}

async function savePushSubscription(subscription, unionId) {
  requireDoroPartyOrigin();
  const normalizedUnionId = normalizeUnionId(unionId);
  const payload = subscription.toJSON();
  const client = requireClient();
  const { data: authData, error: authError } = await client.auth.getUser();
  if (authError || !authData.user) throw authError || new Error('로그인 정보를 확인할 수 없습니다.');

  const { error } = await client.from('push_subscriptions').upsert({
    union_id: normalizedUnionId,
    endpoint: subscription.endpoint,
    p256dh: payload.keys?.p256dh || '',
    auth: payload.keys?.auth || '',
    user_agent: navigator.userAgent,
    active: true,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'endpoint' });
  if (error) throw error;
  notifyPushSubscriptionChanged();
  return subscription;
}

export function pwaSupported() {
  return 'serviceWorker' in navigator;
}

export function pushSupported() {
  return pwaSupported()
    && window.location.origin === DOROPARTY_ORIGIN
    && 'PushManager' in window
    && 'Notification' in window;
}

export async function registerDororaidServiceWorker() {
  if (!pwaSupported()) return null;
  if (window.location.origin !== DOROPARTY_ORIGIN) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister().catch(() => false)));
    return null;
  }
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

export async function getCurrentPushSubscription() {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function syncCurrentPushSubscription({ unionId = '' } = {}) {
  const subscription = await getCurrentPushSubscription();
  if (!subscription) return null;
  return savePushSubscription(subscription, unionId);
}

export async function subscribeToPush({ unionId = '' } = {}) {
  if (!pushSupported()) throw new Error('이 브라우저는 웹 푸시를 지원하지 않습니다.');
  requireDoroPartyOrigin();
  normalizeUnionId(unionId);
  const publicKey = String(import.meta.env.VITE_VAPID_PUBLIC_KEY || DEFAULT_VAPID_PUBLIC_KEY).trim();
  if (!publicKey) throw new Error('VAPID 공개키 설정이 필요합니다.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('알림 권한이 허용되지 않았습니다.');

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription = existing || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToUint8Array(publicKey),
  });

  try {
    return await savePushSubscription(subscription, unionId);
  } catch (error) {
    if (!existing) await subscription.unsubscribe().catch(() => false);
    throw error;
  }
}

export async function unsubscribeFromPush() {
  const subscription = await getCurrentPushSubscription();
  if (!subscription) return false;
  const client = requireClient();
  const { error } = await client.from('push_subscriptions')
    .update({ active: false, updated_at: new Date().toISOString() })
    .eq('endpoint', subscription.endpoint);
  if (error) throw error;
  const unsubscribed = await subscription.unsubscribe();
  notifyPushSubscriptionChanged();
  return unsubscribed;
}

export async function sendUnionPush({ title, body, url = '/', targetUserIds = [] }) {
  const client = requireClient();
  const normalizedTargetUserIds = [...new Set((Array.isArray(targetUserIds) ? targetUserIds : [])
    .map((value) => String(value || '').trim()).filter(Boolean))].slice(0, 40);
  const { data, error } = await client.functions.invoke('send-union-push', {
    body: { title, body, url: normalizeDoroPartyUrl(url), targetUserIds: normalizedTargetUserIds },
  });
  if (error) throw error;
  return data || {};
}
